import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '@/components/AppIcon';
import { RefereeCertification, resolveEnum } from '@/models/tournament';
import type { RefereeView, RefereeScheduleView } from '@/models/tournament';
import { getRefereeSchedule } from '@/api/TournamentManagement/refereeApi';
import ScheduleCalendar from '@/components/tournament/ScheduleCalendar';
import { Star } from 'lucide-react';

type PanelTab = 'details' | 'schedule';

interface RefereeDetailPanelProps {
  referee: RefereeView;
  onClose: () => void;
}

/**
 * The API serializes enums as STRINGS ("FIFAInternational"), but the label/colour maps below are
 * keyed by the NUMERIC enum. Indexing them with the raw wire value silently yields undefined, so
 * the certification badge rendered blank (or "Unknown") on every row. resolveEnum bridges both.
 */
const certOf = (v: unknown) => resolveEnum(RefereeCertification, v) as RefereeCertification;

const certificationLabels: Record<RefereeCertification, string> = {
  [RefereeCertification.NationalLevelA]: 'National Level A',
  [RefereeCertification.AFCElite]: 'AFC Elite',
  [RefereeCertification.FIFAInternational]: 'FIFA International',
};

const certificationColors: Record<RefereeCertification, string> = {
  [RefereeCertification.NationalLevelA]: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  [RefereeCertification.AFCElite]: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  [RefereeCertification.FIFAInternational]: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

const RefereeDetailPanel: React.FC<RefereeDetailPanelProps> = ({ referee, onClose }) => {
  const { t } = useTranslation('tournament');
  const [activeTab, setActiveTab] = useState<PanelTab>('details');
  const [scheduleEntries, setScheduleEntries] = useState<RefereeScheduleView[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  // lastRange: guaranteed non-null when Retry is pressed (error is only set
  // after fetchSchedule runs, which always sets lastRange.current first).
  const lastRange = useRef<{ start: string; end: string } | null>(null);

  // Reset all schedule state when referee changes.
  useEffect(() => {
    setActiveTab('details');
    setScheduleEntries([]);
    setScheduleError(null);
    lastRange.current = null;
  }, [referee.id]);

  const fetchSchedule = useCallback(async (start: string, end: string) => {
    lastRange.current = { start, end };
    setScheduleLoading(true);
    setScheduleError(null);
    try {
      const result = await getRefereeSchedule(referee.id, start, end);
      if (result.errors.length === 0 && result.payload) {
        setScheduleEntries(result.payload);
      } else {
        setScheduleError(result.errors[0]?.description || t('Failed to load schedule'));
      }
    } catch {
      setScheduleError(t('Network error. Please try again.'));
    } finally {
      setScheduleLoading(false);
    }
  }, [referee.id, t]);

  // Tab buttons call setActiveTab directly.
  // When activeTab changes to 'schedule', ScheduleCalendar mounts and its
  // internal useEffect fires onRangeChange (= fetchSchedule) on mount.

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalf = rating - fullStars >= 0.5;
    return (
      <span className="inline-flex items-center gap-0.5">
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={i}
            size={16}
            className={
              i < fullStars ? 'text-amber-400 fill-amber-400'
              : i === fullStars && hasHalf ? 'text-amber-400 fill-amber-400/50'
              : 'text-muted-foreground/40'
            }
          />
        ))}
        <span className="text-sm text-muted-foreground ms-1.5">{rating.toFixed(1)}</span>
      </span>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center sm:justify-end z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="referee-detail-panel"
    >
      <div className="bg-card w-full sm:w-[40vw] sm:max-w-[600px] sm:min-w-[400px] sm:h-full sm:max-h-screen overflow-y-auto border-s border-border shadow-xl animate-in slide-in-from-end sm:slide-in-from-end">
        <div className="sticky top-0 bg-card border-b border-border px-5 py-4 z-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-foreground">{t('Official Details')}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              data-testid="referee-detail-close-btn"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
          <div className="flex gap-1">
            {(['details', 'schedule'] as PanelTab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === tab
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                data-testid={`referee-panel-tab-${tab}`}
              >
                <Icon
                  name={tab === 'details' ? 'User' : 'Calendar'}
                  size={14}
                  className="inline-block me-1.5 align-text-bottom"
                />
                {tab === 'details' ? t('Details') : t('Schedule')}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5">
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="flex flex-col items-center text-center">
                {referee.photoUrl ? (
                  <img src={referee.photoUrl} alt={referee.name}
                    className="w-20 h-20 rounded-full object-cover mb-3 ring-2 ring-amber-200 dark:ring-amber-800" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center mb-3 ring-2 ring-amber-200 dark:ring-amber-800">
                    <Icon name="User" size={32} className="text-amber-600 dark:text-amber-400" />
                  </div>
                )}
                <h3 className="text-lg font-semibold text-foreground">{referee.name}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${certificationColors[certOf(referee.certification)]}`}>
                    {t(certificationLabels[certOf(referee.certification)])}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-foreground">{referee.totalMatches}</div>
                  <div className="text-xs text-muted-foreground">{t('Total Matches')}</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="flex justify-center mb-1">{renderStars(referee.rating)}</div>
                  <div className="text-xs text-muted-foreground">{t('Rating')}</div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Icon name="Coins" size={16} className="text-muted-foreground flex-shrink-0" />
                  <div>
                    <span className="text-muted-foreground">{t('Point to Currency Rate')}</span>
                    <p className="text-foreground" data-testid="referee-detail-currency-rate">
                      {referee.pointToCurrencyRate != null && referee.pointToCurrencyRate > 0
                        ? t('{{rate}} per point (custom, overrides the company-wide rate)', { rate: referee.pointToCurrencyRate })
                        : t('Company-wide rate (Work Point Settings)')}
                    </p>
                  </div>
                </div>
                {referee.location && (
                  <div className="flex items-center gap-3 text-sm">
                    <Icon name="MapPin" size={16} className="text-muted-foreground flex-shrink-0" />
                    <div>
                      <span className="text-muted-foreground">{t('Location')}</span>
                      <p className="text-foreground">{referee.location}</p>
                    </div>
                  </div>
                )}
                {referee.email && (
                  <div className="flex items-center gap-3 text-sm">
                    <Icon name="Mail" size={16} className="text-muted-foreground flex-shrink-0" />
                    <div>
                      <span className="text-muted-foreground">{t('Email (Read-only)')}</span>
                      <p className="text-foreground">{referee.email}</p>
                    </div>
                  </div>
                )}
                {referee.phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Icon name="Phone" size={16} className="text-muted-foreground flex-shrink-0" />
                    <div>
                      <span className="text-muted-foreground">{t('Phone (Read-only)')}</span>
                      <p className="text-foreground">{referee.phone}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'schedule' && (
            scheduleError ? (
              <div className="bg-card border border-border rounded-lg p-8 text-center" data-testid="schedule-error">
                <Icon name="AlertCircle" size={40} className="mx-auto text-red-400 mb-3" />
                <p className="text-sm font-medium text-foreground mb-1">{t('Failed to load schedule')}</p>
                <p className="text-xs text-muted-foreground mb-4">{scheduleError}</p>
                <button
                  onClick={() => {
                    setScheduleError(null);
                    fetchSchedule(lastRange.current!.start, lastRange.current!.end);
                  }}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="schedule-retry-btn"
                >
                  {t('Retry')}
                </button>
              </div>
            ) : (
              <ScheduleCalendar
                entries={scheduleEntries}
                loading={scheduleLoading}
                readOnly={true}
                onRangeChange={fetchSchedule}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default RefereeDetailPanel;
